import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Divider,
  Button,
  Skeleton,
} from '@mui/material';
import SelectedRowDisplay from './SelectedRowDisplay';
import CommentsComponent from './Comments';
import type { IRow } from '../types/TableRows';
import { DatePicker } from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { formatDate } from '../utils/format-date';
import { useCreateNewComment } from '../hooks/useCreateComment';
import { useGetReconDataDetailsQuery } from '../services/reconApiSlice';

interface CommentModalProps {
  open: boolean;
  comment: string;
  breakId: string;
  selectedRow: IRow | null;
  onClose: () => void;
  onSave: () => void;
  onCommentChange: (value: string) => void;
  currentData: IRow[] | undefined;
  refetch: () => unknown;
}

const CommentModal = ({
  open,
  comment,
  breakId,
  onClose,
  onCommentChange,
  currentData,
  refetch,
}: CommentModalProps) => {
  const { triggerCreateNewComment } = useCreateNewComment();

  const [singleDate, setSingleDate] = useState<Date | null>(null);

  const [hasClearedDate, setHasClearedDate] = useState(false);

  const detailData = currentData?.find((data) => data.breakId === breakId);

  const effectiveDate = hasClearedDate
    ? null
    : (singleDate ?? (detailData?.nextReviewDate ? new Date(detailData.nextReviewDate) : null));

  const currDate = effectiveDate ? formatDate(effectiveDate) : '';

  const handleSingleDateChange = (newValue: Date | null) => {
    if (newValue === null) {
      setHasClearedDate(true);
      setSingleDate(null);
    } else {
      setHasClearedDate(false);
      setSingleDate(newValue);
    }
  };

  const handleAddComment = async () => {
    const newComment = {
      breakId,
      comment,
    };

    await triggerCreateNewComment(newComment, currDate);

    setSingleDate(null);

    refetch();
    onClose();
  };

  const { data, isFetching, refetch: refetchDetails } = useGetReconDataDetailsQuery({ breakId });

  useEffect(() => {
    if (open) {
      void refetchDetails();
    }
  }, [open, refetchDetails]);

  const skeletonLoad = (n: number) => {
    return Array.from({ length: n }, (_, i) => (
      <div key={i} className="flex justify-between gap-5">
        <Skeleton height={50} width="49%" />
        <Skeleton height={50} width="49%" />
      </div>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" disableRestoreFocus>
      <DialogTitle>
        <Typography>Add Comment for Row</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {isFetching ? skeletonLoad(7) : <SelectedRowDisplay data={data} />}

        <div className="my-5 flex items-center gap-2 text-lg font-bold">
          <div>Next Review Date:</div>

          <DatePicker
            key={open ? 'open' : 'closed'}
            label="Next Review Date"
            value={
              hasClearedDate
                ? null
                : (singleDate ?? (data?.nextReviewDateUTC ? new Date(`${data.nextReviewDateUTC}T00:00:00`) : null))
            }
            onChange={handleSingleDateChange}
            format="MM/dd/yyyy"
            slotProps={{
              field: { clearable: true },
            }}
          />
        </div>
        <TextField
          autoFocus
          sx={{ py: 2 }}
          fullWidth
          multiline
          rows={4}
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder="Enter your comment"
        />
        <Typography>Comment History</Typography>
        <Divider sx={{ my: 2 }} />
        <CommentsComponent breakId={breakId} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void handleAddComment()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommentModal;
